import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { AppState } from 'src/app/state/app.state';
import { personaFeature, PersonaFetch } from '@workspaces/store/persona.feature';
import { Persona } from 'src/app/models/personas.interface';

@Component({
  selector: 'app-persona-selector',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatListModule, MatIconModule, CdkDropList, CdkDrag],
  templateUrl: './persona-selector.component.html',
  styleUrls: ['./persona-selector.component.scss']
})
export class PersonaSelectorComponent implements OnInit, OnDestroy {
  private _initialSelectedPersonaIds: string[] | null = [];
  @Input() set initialSelectedPersonaIds(value: string[] | null) {
    this._initialSelectedPersonaIds = value;
    this.dividePersonas();
  }
  get initialSelectedPersonaIds(): string[] | null {
    return this._initialSelectedPersonaIds;
  }
  @Input() disabled = false;
  @Output() selectionChange = new EventEmitter<string[]>();

  public unusedPersonas: Persona[] = [];
  public usedPersonas: Persona[] = [];
  public loading$!: Observable<boolean>;

  private allPersonas: Persona[] = [];
  private readonly sub = new Subscription();

  constructor(private readonly store: Store<AppState>) {}

  ngOnInit(): void {
    this.store.dispatch(PersonaFetch({ includeHidden: true }));
    this.loading$ = this.store.select(personaFeature.personaIsLoading);

    this.sub.add(
      this.store.select(personaFeature.selectData).subscribe(allPersonas => {
        this.allPersonas = allPersonas;
        this.dividePersonas();
      })
    );
  }

  dividePersonas(): void {
    if (!this.allPersonas.length) {
      return;
    }

    this.unusedPersonas = [];
    this.usedPersonas = [];

    const idsToSelect = this.initialSelectedPersonaIds || [];
    const usedIds = new Set(idsToSelect);

    const usedMap = new Map<string, Persona>();
    const unusedMap = new Map<string, Persona>();

    this.allPersonas.forEach(persona => {
      if (usedIds.has(persona.id!)) {
        usedMap.set(persona.id!, persona);
      } else {
        unusedMap.set(persona.id!, persona);
      }
    });

    const newUsed: Persona[] = [];
    idsToSelect.forEach(id => {
      if (usedMap.has(id)) {
        newUsed.push(usedMap.get(id)!);
        usedMap.delete(id);
      }
    });

    usedMap.forEach(persona => newUsed.push(persona));

    this.usedPersonas = newUsed;
    this.unusedPersonas = Array.from(unusedMap.values());
  }

  drop(event: CdkDragDrop<Persona[]>) {
    if (this.disabled) return;

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);
    }
    this.emitSelection();
  }

  private emitSelection(): void {
    const ids = this.usedPersonas.map(p => p.id!).filter(id => !!id);
    this.selectionChange.emit(ids);
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
